import { useState } from 'react';

import Button from '../common/button';
import { PaginationControls } from '../common/pagination-controls';

interface Service {
  featureCode?: string;
  name: string;
  available: boolean;
  used?: number;
  limit?: number;
  unit?: string;
}

interface UsageEvent {
  date: string;
  feature: {
    id: string | null;
    code: string | null;
    description: string | null;
  };
  quantity: number;
  unit: string;
  metadata: Record<string, unknown> | null;
}

interface ActiveAddon {
  name: string;
  amount: number;
  unit: string;
  used?: number;
  limit?: number;
  expiresAt: string;
}

interface ActiveSubscriptionProps {
  plan: string;
  billingCycle: 'monthly' | 'annual';
  price: number;
  durationDays?: number | null;
  nextBillingDate?: string | null;
  scheduledPlanName?: string;
  scheduledStartAt?: string;
  services: Service[];
  usageEvents?: UsageEvent[];
  availableFeatures?: string[];
  activeAddons?: ActiveAddon[];
  onUpgrade: () => void;
  onCancel: () => void;
  onBuyAddons?: () => void;
  hideAddons?: boolean;
  showCancel?: boolean;
  cancelLoading?: boolean;
  upgradeActionLabel?: string;
  onActivateNow?: () => void;
  activateNowLoading?: boolean;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(value);
const formatDate = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' });
};

const ProgressBar = ({ value }: { value: number }) => (
  <div className="h-2 w-full rounded-full bg-greyWithOpacity30 overflow-hidden">
    <div className="h-full bg-redBase" style={{ width: `${Math.min(100, Math.max(0, value))}%` }} />
  </div>
);

export function ActiveSubscription({
  plan,
  billingCycle,
  price,
  durationDays,
  nextBillingDate,
  scheduledPlanName,
  scheduledStartAt,
  services,
  usageEvents = [],
  availableFeatures = [],
  activeAddons = [],
  onUpgrade,
  onCancel,
  onBuyAddons,
  hideAddons = false,
  showCancel = false,
  cancelLoading = false,
  upgradeActionLabel = 'Продлить тариф',
  onActivateNow,
  activateNowLoading = false,
}: ActiveSubscriptionProps) {
  const [expandedFeatureKeys, setExpandedFeatureKeys] = useState<Record<string, boolean>>({});
  const [featurePages, setFeaturePages] = useState<Record<string, number>>({});
  const pageSize = 10;

  const toggleExpanded = (key: string) => {
    setExpandedFeatureKeys((prev) => {
      const nextExpanded = !prev[key];
      if (nextExpanded) {
        setFeaturePages((pages) => ({ ...pages, [key]: 0 }));
      }
      return { ...prev, [key]: nextExpanded };
    });
  };

  const formatUsageDate = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      <div className="rounded-[24px] bg-white border border-[#EFEFEF] shadow-[0px_10px_30px_rgba(0,0,0,0.05)] p-6">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <h2 className="font-comfortaa text-xl lg:text-2xl">Мой тариф: {plan}</h2>
              <span className="px-3 py-1 text-xs font-semibold rounded-full bg-green-600 text-white">Активно</span>
            </div>
          </div>
          <div className="text-right">
            <p className="text-3xl font-semibold text-blackBase">{formatCurrency(price)}</p>
            {typeof durationDays === 'number' && durationDays > 0 && (
              <p className="text-sm text-grey1">за {durationDays} дней</p>
            )}
          </div>
        </div>

        {nextBillingDate && (
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
            <div className="flex items-center gap-3 p-3 rounded-[16px] bg-grey5 flex-1">
              <div className="flex items-center justify-center size-9 rounded-[12px] bg-white text-redBase border border-[#EFEFEF]">
                <CalendarIcon />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-grey2">Активно до</p>
                <p className="font-semibold text-blackBase">{nextBillingDate}</p>
              </div>
            </div>
            {!scheduledPlanName && (
              <Button type="bordered" className="!w-auto !px-5 !text-sm md:shrink-0" onClick={onUpgrade}>
                {upgradeActionLabel}
              </Button>
            )}
          </div>
        )}

        {scheduledPlanName && (
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="font-comfortaa text-xl lg:text-2xl">Запланированный тариф: {scheduledPlanName}</h3>
              {formatDate(scheduledStartAt) && (
                <p className="text-sm text-grey1">Активно с {formatDate(scheduledStartAt)}</p>
              )}
            </div>
            {onActivateNow && (
              <Button
                type="bordered"
                className="!w-auto !px-5 !text-sm md:shrink-0"
                onClick={onActivateNow}
                loading={activateNowLoading}
              >
                Активировать сейчас
              </Button>
            )}
          </div>
        )}

        {showCancel && (
          <div className="flex justify-end mt-4">
            <Button
              type="bordered"
              className="!w-auto !px-5 !text-sm"
              onClick={onCancel}
              loading={cancelLoading}
            >
              Отменить тариф
            </Button>
          </div>
        )}
        {services.length > 0 && (
          <div className="mt-6">
            <div className="grid grid-cols-1 gap-4">
              {services.map((service, index) => (
                <div
                  key={`${service.featureCode ?? service.name}-${index}`}
                  className={`overflow-hidden rounded-[20px] border p-4 shadow-[0px_8px_20px_rgba(17,16,38,0.04)] ${
                    service.available ? 'border-[#B5ECA1] bg-[#F6FBF7]' : 'border-[#EFEFEF] bg-grey5'
                  }`}
                >
                  {(() => {
                    const featureKey = service.featureCode ?? service.name;
                    const relatedEvents = usageEvents.filter((event) => {
                      const eventCode = event.feature?.code ?? null;
                      const eventDescription = event.feature?.description ?? null;
                      return (
                        (service.featureCode && eventCode === service.featureCode) ||
                        eventDescription === service.name
                      );
                    });
                    const expanded = Boolean(expandedFeatureKeys[featureKey]);
                    const page = featurePages[featureKey] ?? 0;
                    const totalPages = Math.max(1, Math.ceil(relatedEvents.length / pageSize));
                    const pagedEvents = relatedEvents.slice(page * pageSize, (page + 1) * pageSize);

                    return (
                      <>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <span className="min-w-0 break-words font-comfortaa text-lg font-semibold text-blackBase">{service.name}</span>
                      <span
                        className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
                          service.available ? 'bg-green-600 text-white' : 'bg-white text-grey2 border border-[#E6E6E6]'
                        }`}
                      >
                        {service.available ? 'Активно' : 'Недоступно'}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="min-h-11 self-start shrink-0 rounded-full border border-[#E6E6E6] bg-white px-4 text-sm font-semibold text-grey2 transition-colors hover:bg-[#F7F7F7] focus-visible:ring-2 focus-visible:ring-redBase focus-visible:ring-offset-2 sm:self-auto"
                      onClick={() => toggleExpanded(featureKey)}
                    >
                      Подробнее
                    </button>
                  </div>

                  <div className="mt-5 space-y-2">
                    <div className="flex flex-col gap-1 text-sm sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                      <span className="text-grey1">Использовано</span>
                      <span className="break-words font-semibold text-blackBase sm:text-right">
                        {(service.used || 0).toLocaleString('ru-RU')} из {(service.limit || 0).toLocaleString('ru-RU')}{' '}
                        {service.unit}
                      </span>
                    </div>
                    <div className="mt-2.5">
                      <ProgressBar value={(service.limit || 0) > 0 ? ((service.used || 0) / (service.limit || 1)) * 100 : 0} />
                    </div>
                  </div>
                        {expanded && (
                          <div className="mt-4 overflow-x-auto">
                            <table className="min-w-full text-sm border border-[#E6E6E6] rounded-[12px] overflow-hidden">
                              <thead className="bg-white">
                                <tr>
                                  <th className="text-left px-3 py-2 border-b border-[#E6E6E6]">Дата</th>
                                  <th className="text-left px-3 py-2 border-b border-[#E6E6E6]">Код сессии</th>
                                  <th className="text-left px-3 py-2 border-b border-[#E6E6E6]">Списано</th>
                                </tr>
                              </thead>
                              <tbody>
                                {relatedEvents.length > 0 ? (
                                  pagedEvents.map((event, eventIndex) => (
                                    <tr key={`${featureKey}-${event.date}-${eventIndex}`} className="bg-white">
                                      <td className="px-3 py-2 border-b border-[#F1F1F1]">{formatUsageDate(event.date)}</td>
                                      <td className="px-3 py-2 border-b border-[#F1F1F1]">
                                        {String(event.metadata?.consultationCode ?? '-')}
                                      </td>
                                      <td className="px-3 py-2 border-b border-[#F1F1F1]">
                                        {Number(event.quantity ?? 0).toLocaleString('ru-RU')} {event.unit}
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr className="bg-white">
                                    <td className="px-3 py-2 text-grey1" colSpan={3}>
                                      История использования отсутствует
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                            {relatedEvents.length > pageSize && (
                              <PaginationControls
                                page={page + 1}
                                totalPages={totalPages}
                                keyPrefix={`${featureKey}-page`}
                                maxVisiblePages={3}
                                onPageChange={(pageNumber) =>
                                  setFeaturePages((pages) => ({
                                    ...pages,
                                    [featureKey]: pageNumber - 1,
                                  }))
                                }
                              />
                            )}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              ))}
            </div>
          </div>
        )}
        {availableFeatures.length > 0 && (
          <div className="mt-6">
            <h3 className="font-comfortaa mb-3">Доступные функции</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {availableFeatures.map((feature, index) => (
                <div key={`${feature}-${index}`} className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center text-green-600">
                    <CheckIcon />
                  </span>
                  <span className="text-blackBase">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        </div>

      {!hideAddons && activeAddons.length > 0 && (
        <div className="rounded-[24px] bg-white border border-[#EFEFEF] shadow-[0px_10px_30px_rgba(0,0,0,0.05)] p-6">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <h3 className="font-comfortaa">Активные дополнения</h3>
            <Button type="bordered" className="!w-auto !px-4 !text-sm" onClick={onBuyAddons}>
              Купить еще
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {activeAddons.map((addon, index) => (
              <div
                key={`${addon.name}-${index}`}
                className="rounded-[16px] border border-goldBase bg-[#FFFAEC] p-3"
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-blackBase">{addon.name}</span>
                  <span className="px-3 py-1 text-xs font-semibold rounded-full bg-goldBase text-blackBase">Дополнение</span>
                </div>
                <p className="text-sm text-grey1 mb-1">+{addon.amount.toLocaleString('ru-RU')} {addon.unit}</p>
                {addon.limit !== undefined && addon.limit > 0 && (
                  <div className="space-y-2 mb-2">
                    <div className="flex items-center justify-between text-sm mt-0.5">
                      <span className="text-grey1">Использовано</span>
                      <span className="font-semibold text-blackBase">
                        {(addon.used || 0).toLocaleString('ru-RU')} из {addon.limit.toLocaleString('ru-RU')} {addon.unit}
                      </span>
                    </div>
                    <ProgressBar value={((addon.used || 0) / addon.limit) * 100} />
                  </div>
                )}
                <p className="text-xs text-grey2">Доступно до: {addon.expiresAt}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-[24px] border border-[#D9E2F2] bg-[#F1F6FF] p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h4 className="font-comfortaa mb-2">Нужно больше ресурсов?</h4>
            <p className="text-sm text-grey1">
              Перейдите на тариф выше, чтобы получить больше ресурсов.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button className="!w-auto !px-4 !text-sm" onClick={onUpgrade}>
              Тарифы
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="size-5" aria-hidden="true">
    <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M8 3v4M16 3v4M3 10h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="size-3" aria-hidden="true">
    <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="size-3" aria-hidden="true">
    <path d="M8 8l8 8M16 8l-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
